import React, { useEffect, useState } from "react";
import axios from "../api/axios";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Chip,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";

import CommonTable from "../components/CommonTable";
import { useMessage } from "../context/MessageContext";
import { useConfirm } from "../context/ConfirmContext";

const BranchFormDialog = ({ open, onClose, onSave, branch, orgs }) => {
  const { showMessage } = useMessage();

  const [form, setForm] = useState({
    name: "",
    code: "",
    organization: "",
    grades: [],
    programs: [],
  });

  const [gradesList, setGradesList] = useState([]);
  const [programsList, setProgramsList] = useState([]);

  useEffect(() => {
    if (open) {
      if (branch) {
        setForm({ ...branch, organization: branch?.organization?._id });
      } else {
        setForm({
          name: "",
          code: "",
          address: "",
          organization: "",
          grades: [],
          programs: [],
        });
      }
    }
  }, [open, branch]);

  useEffect(() => {
    if (form.organization) fetchMasters(form.organization);
  }, [form.organization]);

  const fetchMasters = async (orgId) => {
    try {
      const res = await axios.get(`/masters/${orgId}`);
      setGradesList(res.data.grades || []);
      setProgramsList(res.data.programs || []);
    } catch (err) {
      console.error(err);
      showMessage("Failed to load master data", "error");
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleMultiChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    if (!form.organization) {
      showMessage("Organization is required", "warning");
      return false;
    }
    if (!form.name.trim()) {
      showMessage("Branch name is required", "warning");
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSave(form);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{branch ? "Edit" : "Add"} Branch</DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="normal">
          <InputLabel>Organization</InputLabel>
          <Select
            name="organization"
            value={form.organization}
            onChange={handleChange}
            required
            label="Organization"
          >
            {orgs.map((org) => (
              <MenuItem key={org._id} value={org._id}>
                {org.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Name"
          name="name"
          value={form.name}
          onChange={handleChange}
          fullWidth
          margin="normal"
          required
        />
        <TextField
          label="Code"
          name="code"
          value={form.code}
          onChange={handleChange}
          fullWidth
          margin="normal"
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Grades</InputLabel>
          <Select
            multiple
            name="grades"
            value={form.grades}
            onChange={handleMultiChange}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} />
                ))}
              </Box>
            )}
            label="Grades"
          >
            {gradesList.map((grade) => (
              <MenuItem key={grade} value={grade}>
                {grade}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel>Programs</InputLabel>
          <Select
            multiple
            name="programs"
            value={form.programs}
            onChange={handleMultiChange}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} />
                ))}
              </Box>
            )}
            label="Programs"
          >
            {programsList.map((prog) => (
              <MenuItem key={prog} value={prog}>
                {prog}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          {branch ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const BranchSetup = () => {
  const { showMessage } = useMessage();
  const { confirm } = useConfirm();

  const [branches, setBranches] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState("");
  const [open, setOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOrgs = async () => {
    try {
      const res = await axios.get("/orgs");
      setOrgs(res.data);
      if (res.data[0]) setSelectedOrg(res.data[0]._id);
    } catch (err) {
      console.error(err);
      showMessage("Failed to load organizations", "error");
    }
  };

  const fetchBranches = async (orgId) => {
    if (!orgId) return;
    try {
      setLoading(true);
      const res = await axios.get("/branches", {
        params: { orgId },
      });
      setBranches(res.data);
    } catch (err) {
      console.error(err);
      showMessage("Failed to load branches", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrgs();
  }, []);

  useEffect(() => {
    if (selectedOrg) fetchBranches(selectedOrg);
  }, [selectedOrg]);

  const handleAdd = () => {
    setEditingBranch(null);
    setOpen(true);
  };

  const handleEdit = (branch) => {
    setEditingBranch(branch);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm({
      title: "Confirm Delete",
      message: "Are you sure you want to delete this branch?",
    });
    if (confirmed) {
      try {
        await axios.delete(`/branches/${id}`);
        showMessage("Branch deleted", "success");
        fetchBranches(selectedOrg);
      } catch (err) {
        console.error(err);
        showMessage("Failed to delete branch", "error");
      }
    }
  };

  const handleSave = async (form) => {
    if (!form.name.trim()) {
      showMessage("Branch name is required", "warning");
      return;
    }
    if (!form.code.trim()) {
      showMessage("Branch code is required", "warning");
      return;
    }
    if (!form.organization) {
      showMessage("Organization is required", "warning");
      return;
    }
    try {
      if (editingBranch) {
        await axios.put(`/branches/${editingBranch._id}`, form);
        showMessage("Branch updated", "success");
      } else {
        await axios.post("/branches", form);
        showMessage("Branch created", "success");
      }
      setOpen(false);
      fetchBranches(selectedOrg);
    } catch (err) {
      console.error(err);
      showMessage("Failed to save branch", "error");
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Branches
      </Typography>

      <FormControl sx={{ my: 2, mr: 1, minWidth: 240 }}>
        <InputLabel>Organization</InputLabel>
        <Select
          value={selectedOrg}
          label="Organization"
          onChange={(e) => setSelectedOrg(e.target.value)}
        >
          {orgs.map((org) => (
            <MenuItem key={org._id} value={org._id}>
              {org.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button variant="contained" onClick={handleAdd} sx={{ my: 2 }}>
        Add Branch
      </Button>

      <CommonTable
        columns={[
          { label: "Name", field: "name" },
          { label: "Code", field: "code" },
          {
            label: "Organization",
            renderCell: (b) => b.organization?.name || "-",
          },
          { label: "Grades", renderCell: (b) => b.grades?.join(", ") || "-" },
          {
            label: "Programs",
            renderCell: (b) => b.programs?.join(", ") || "-",
          },
          {
            label: "Actions",
            sortable: false,
            renderCell: (branch) => (
              <>
                <Button
                  size="small"
                  onClick={() => handleEdit(branch)}
                  startIcon={<Edit />}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={() => handleDelete(branch._id)}
                  startIcon={<Delete />}
                >
                  Delete
                </Button>
              </>
            ),
          },
        ]}
        rows={branches}
        rowsPerPageOptions={[10, 25]}
      />

      <BranchFormDialog
        open={open}
        onClose={() => setOpen(false)}
        onSave={handleSave}
        branch={editingBranch}
        orgs={orgs}
      />
    </Box>
  );
};

export default BranchSetup;
